import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, InputAdornment, IconButton, Checkbox } from '@mui/material';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import { FaPlus, FaSearch, FaCogs, FaTrash, FaGraduationCap, FaLayerGroup, FaTools } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { skillsApi } from '../../../../../../services/api';
import { useAlert } from '../../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../../common/LoadingSpinner';
import Modal from '../../../../../common/Modal';
import EmptyState from '../../../../../common/EmptyState';
import ConfirmationModal from '../../../../../common/ConfirmationModal';
import { SKILL_TYPES, getStatusColor, getStatusText, getStatusTextColor } from '../../../../../../constants/skillTypes';
import { COLORS } from '../../../../../../constants/radar';
import { getCompetenciesStatus } from '../../../../../../utils/competenciesHelper';

const SkillsSelection = ({ isTechnicalAssessment }) => {
  const { matrixId, versionId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [modalState, setModalState] = useState({
    open: false,
    skillType: null,
    searchTerm: '',
    availableSkills: [],
    selectedSkill: null,
    loading: false
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, skill: null });

  const fetchData = async () => {
    try {
      const data = await skillsApi.getTemplateById(versionId);
      // Add additional fields and split by skill type
      const processedSkills = data.skillsTemplates.map(template => ({
        ...template,
        skillName: template.skill?.name || 'Unknown Skill',
        skillDescription: template.skill?.description || '',
        segmentsCount: template.mappings.length,
        segments: template.mappings.length > 0 ? `${template.mappings.length} ${template.mappings.length === 1 ? 'Technology' : 'Technologies'}` : 'No assignments',
        isRequired: template.isRequired !== false // Default to true for backwards compatibility
      }));

      // Filter skills based on type
      setSkills(processedSkills.filter(t => 
        t.skill?.type === (isTechnicalAssessment ? SKILL_TYPES.TECHNICAL : SKILL_TYPES.SOFT)
      ));
    } catch (error) {
      showAlert('Failed to fetch skills', 'error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [versionId, showAlert]);

  const searchSkills = async (searchTerm, skillType) => {
    try {
      setModalState(prev => ({ ...prev, loading: true }));
      const allSkills = await skillsApi.getAllSkills();
      const filteredSkills = allSkills
        .filter(skill => 
          skill.type === skillType && 
          (skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           skill.description?.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        // Filter out skills that are already attached
        .filter(skill => !skills.some(existing => existing.skillId === skill.id));

      setModalState(prev => ({ 
        ...prev, 
        availableSkills: filteredSkills,
        loading: false 
      }));
    } catch (error) {
      showAlert('Failed to search skills', 'error', error);
      setModalState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAttachSkill = async () => {
    if (!modalState.selectedSkill) {
      showAlert('Please select a skill to attach', 'error');
      return;
    }

    try {
      await skillsApi.attachSkillToTemplate(versionId, {
        skillId: modalState.selectedSkill.id,
        weight: 0, // Default weight
        isRequired: true // Default to required
      });
      
      showAlert('Skill attached successfully', 'success');
      setModalState(prev => ({ ...prev, open: false }));
      // Refresh the data
      fetchData();
    } catch (error) {
      showAlert(error.message || 'Failed to attach skill', 'error', error);
    }
  };

  const handleRemoveSkill = async () => {
    try {
      await skillsApi.removeSkillFromTemplate(versionId, deleteModal.skill.skillId);
      showAlert('Skill removed successfully', 'success');
      setDeleteModal({ open: false, skill: null });
      fetchData();
    } catch (error) {
      showAlert(error.message || 'Failed to remove skill', 'error', error);
    }
  };

  const columns = [
    {
      field: 'skillName',
      headerName: 'Skill',
      width: 350,
      editable: false,
      renderCell: (params) => (
        <Box
          sx={{ 
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'help'
          }}
          title={params.row.skillDescription || 'No description provided'}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'weight',
      headerName: 'Weight',
      type: 'number',
      width: 80,
      editable: true
    },
    {
      field: 'isRequired',
      headerName: 'Required',
      width: 100,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          disabled
          sx={{
            color: COLORS.PRIMARY,
            '&.Mui-checked': { color: COLORS.PRIMARY },
            '&.Mui-disabled': { color: params.value ? COLORS.PRIMARY : undefined }
          }}
        />
      )
    },
    {
      field: 'segments',
      headerName: 'Assignments',
      width: 200,
      editable: false,
      renderCell: (params) => (
        <Typography
          sx={{
            fontStyle: params.row.segmentsCount === 0 ? 'italic' : 'normal',
            color: params.row.segmentsCount === 0 ? COLORS.TEXT_LIGHT : 'inherit',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.875rem'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'competenciesStatus',
      headerName: 'Competencies',
      width: 200,
      editable: false,
      renderCell: (params) => {
        const status = getCompetenciesStatus(params.row.competencies);

        return (
          <Typography
            sx={{
              fontStyle: !status.hasCompetencies || !status.isComplete ? 'italic' : 'normal',
              color: status.color,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem'
            }}
          >
            {status.message}
          </Typography>
        );
      }
    },
    {
      field: 'technologyCompetenciesStatus',
      headerName: 'Technology Competencies',
      width: 200,
      editable: false,
      renderCell: (params) => {
        // Aggregate all competencies from all mappings (technologies)
        const allTechnologyCompetencies = params.row.mappings?.reduce((acc, mapping) => {
          if (mapping.competencies) {
            return [...acc, ...mapping.competencies];
          }
          return acc;
        }, []) || [];

        const status = getCompetenciesStatus(allTechnologyCompetencies);

        return (
          <Typography
            sx={{
              fontStyle: !status.hasCompetencies || !status.isComplete ? 'italic' : 'normal',
              color: status.color,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem'
            }}
          >
            {status.message}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 240,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          height: '100%',
          alignItems: 'center'
        }}>
          <IconButton
            size="small"
            onClick={() => 
              navigate(`/skills-matrix/templates/${matrixId}/versions/${versionId}/configure/skills/${params.row.skill.id}/competencies`)
            }
            sx={{ 
              color: COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: `${COLORS.PRIMARY}10`
              }
            }}
            title="Configure Competencies"
          >
            <FaGraduationCap />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => 
              navigate(`/skills-matrix/templates/${matrixId}/versions/${versionId}/configure/skills/${params.row.skill.id}/segments`)
            }
            sx={{ 
              color: COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: `${COLORS.PRIMARY}10`
              }
            }}
            title="Configure Technologies"
          >
            <FaLayerGroup />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => 
              navigate(`/skills-matrix/templates/${matrixId}/versions/${versionId}/configure/skills/${params.row.skill.id}/segments/competencies`)
            }
            sx={{ 
              color: COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: `${COLORS.PRIMARY}10`
              }
            }}
            title="Configure Technology Competencies"
          >
            <FaTools />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setDeleteModal({ open: true, skill: params.row })}
            sx={{ 
              color: '#EF4444',
              '&:hover': {
                backgroundColor: '#FEE2E2'
              }
            }}
            title="Remove Skill"
          >
            <FaTrash />
          </IconButton>
        </Box>
      )
    }
  ];

  const handleProcessRowUpdate = async (newRow, oldRow) => {
    // Check if anything actually changed
    const weightChanged = newRow.weight !== oldRow.weight;
    const isRequiredChanged = newRow.isRequired !== oldRow.isRequired;

    if (!weightChanged && !isRequiredChanged) {
      return oldRow; // Return old row without making API call if nothing changed
    }

    try {
      await skillsApi.updateSkillsTemplateWeights(newRow.id, {
        weight: newRow.weight,
        isRequired: newRow.isRequired
      });
      const message = weightChanged && isRequiredChanged
        ? 'Weight and required status updated successfully'
        : weightChanged
          ? 'Weight updated successfully'
          : 'Required status updated successfully';
      showAlert(message, 'success');
      return newRow;
    } catch (error) {
      showAlert('Failed to update skill template', 'error', error);
      return oldRow;
    }
  };

  const CustomToolbar = ({ skillType }) => {
    const handleAddSkill = () => {
      setModalState({
        open: true,
        skillType,
        searchTerm: '',
        availableSkills: [],
        selectedSkill: null,
        loading: false
      });
      // Initial search with empty term
      searchSkills('', skillType);
    };

    return (
      <GridToolbarContainer>
        <Button
          startIcon={<FaPlus />}
          onClick={handleAddSkill}
          sx={{
            color: getStatusTextColor(skillType),
            '&:hover': {
              backgroundColor: `${getStatusTextColor(skillType)}10`
            },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px'
          }}
        >
          Attach Skill
        </Button>
      </GridToolbarContainer>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const skillType = isTechnicalAssessment ? SKILL_TYPES.TECHNICAL : SKILL_TYPES.SOFT;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, justifyContent: 'left' }}>
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          {getStatusText(skillType)} Skills
        </Typography>
        <Box sx={{ 
          flex: 1,
          width: '100%',
          backgroundColor: getStatusColor(skillType),
          borderRadius: '8px',
          padding: '16px',
          '& .MuiDataGrid-root': {
            backgroundColor: 'white',
            borderRadius: '4px'
          },
          '& .MuiDataGrid-cell--editing': {
            bgcolor: 'rgb(255,215,115, 0.19)',
            '& .MuiInputBase-root': {
              height: '100%'
            }
          }
        }}>
          <DataGrid
            rows={skills}
            columns={columns}
            hideFooter
            autoHeight
            processRowUpdate={handleProcessRowUpdate}
            slots={{
              toolbar: () => <CustomToolbar skillType={skillType} />
            }}
          />
        </Box>
      </Box>

      <Modal
        isOpen={modalState.open}
        onClose={() => setModalState(prev => ({ ...prev, open: false }))}
        title={`Attach ${modalState.skillType} Skill`}
        submitText="Attach"
        cancelText="Cancel"
        onSubmit={handleAttachSkill}
        submitDisabled={!modalState.selectedSkill}
        maxWidth="lg"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            placeholder="Search Skills"
            variant="outlined"
            size="small"
            value={modalState.searchTerm}
            onChange={(e) => {
              const newTerm = e.target.value;
              setModalState(prev => ({ ...prev, searchTerm: newTerm }));
              searchSkills(newTerm, modalState.skillType);
            }}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: '#6B7280' }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ 
            height: '400px',
            overflowY: 'auto',
            p: 2,
            mb: 2,
            border: '1px solid #E2E8F0',
            borderRadius: '8px'
          }}>
            {modalState.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <LoadingSpinner />
              </Box>
            ) : modalState.availableSkills.length === 0 ? (
              <EmptyState
                title="No skills found"
                description={modalState.searchTerm ? 
                  "No skills match your search criteria. Try adjusting your search term." : 
                  "No skills available to attach."}
              />
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 2
              }}>
                {modalState.availableSkills.map((skill) => (
                  <Box
                    key={skill.id}
                    onClick={() => setModalState(prev => ({ 
                      ...prev, 
                      selectedSkill: prev.selectedSkill?.id === skill.id ? null : skill 
                    }))}
                    sx={{
                      border: '1px solid',
                      borderColor: skill.id === modalState.selectedSkill?.id ? 
                        getStatusTextColor(modalState.skillType) : '#E2E8F0',
                      borderRadius: '8px',
                      p: 2,
                      cursor: 'pointer',
                      backgroundColor: skill.id === modalState.selectedSkill?.id ? 
                        `${getStatusColor(modalState.skillType)}` : 'white',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: getStatusTextColor(modalState.skillType),
                        backgroundColor: skill.id === modalState.selectedSkill?.id ? 
                          `${getStatusColor(modalState.skillType)}` : 
                          `${getStatusColor(modalState.skillType)}50`,
                      }
                    }}
                  >
                    <Typography
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 500,
                        color: skill.id === modalState.selectedSkill?.id ? 
                          getStatusTextColor(modalState.skillType) : COLORS.PRIMARY
                      }}
                    >
                      {skill.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748B',
                        mt: 1,
                        fontSize: '13px',
                        lineHeight: 1.4
                      }}
                    >
                      {skill.description || <em>No description provided</em>}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, skill: null })}
        onConfirm={handleRemoveSkill}
        title="Remove Skill"
        message={`Are you sure you want to remove "${deleteModal.skill?.skillName}" from the template? This action cannot be undone.`}
      />
    </Box>
  );
};

export default SkillsSelection; 